// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@sc/App/Components/Notify";
import React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    emailSentText?: TypographyPresentationProps;
    divider?: InjectableCss;
    orderInfoGridItem?: GridItemProps;
    orderNumberText?: TypographyPresentationProps;
    orderPlacedText?: TypographyPresentationProps;
    deliveryMethodText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    printButton?: ButtonPresentationProps;
    // XNG-Change
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    customMsg?: TypographyPresentationProps;
    requiresApprovalMsg?: TypographyPresentationProps;
}

export const headerStyles: OnePageCheckoutConfirmationHeaderStyles = {
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    titleText: {
        variant: "h2",
    },
    divider: {
        css: css`
            padding: 15px;
            height: 100%;
            width: 100%;
            overflow: hidden;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderInfoGridItem: {
        width: [8, 8, 10, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryMethodText: {
        css: css`
            margin-top: 10px;
        `,
    },
    orderNumberText: { variant: "h5" },
    buttonsGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            justify-content: flex-end;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
};

const styles = headerStyles;

const Divider = styled.div`
    ${styles.divider?.css}
`;

const OnePageCheckoutConfirmationHeader = ({ cart, billTo }: Props) => {
    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    if (!cart || !billTo) {
        return null;
    }

    // XNG-Change: change on structure to match design and FRD
    return (
        <StyledWrapper {...styles.container}>
            <Typography variant="h1" {...styles.titleText} data-test-selector="orderConfirmation_Success">
                {siteMessage("OrderConfirmation_Success")}
            </Typography>

            <Typography variant="h2" {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                {translate("Order Request Confirmation")} #{cart.erpOrderNumber || cart.orderNumber}
            </Typography>
            <SmallHeadingAndText
                extendedStyles={styles.headingAndTextStyles}
                heading={translate("Order Date")}
                text={getLocalizedDateTime({
                    dateTime: new Date(cart.orderDate as Date),
                    options: { year: "numeric", month: "numeric", day: "numeric" },
                })}
            />
            {cart.requiresApproval && (
                <Typography {...styles.requiresApprovalMsg}>
                    {siteMessage(
                        "Custom_OrderConfirmation_Submitted_For_Approval",
                        cart.erpOrderNumber || cart.orderNumber,
                    )}
                </Typography>
            )}
            <Typography {...styles.customMsg}>{siteMessage("Custom_OrderConfirmation_Msg")}</Typography>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OnePageCheckoutConfirmationHeader),
    definition: {
        displayName: "Header",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
